import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import { Chip, Typography } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import moment from 'moment';
import React from 'react';

import { CAMPAIGN_STATUS, operatorLocalTimeLabel } from './campaignStorage';
import { CampaignActionsColumn } from './CustomDataGridCols';

/**
 * Campaign list grid. Columns mirror the Figma design:
 *   Name | Category | Audience | Sent | Status | Delivery/Open/Reply | Created | Action
 *
 * The Delivery / Open / Reply column surfaces the same rolled-up engagement
 * rates shown on the detail Results tab, but only for sent campaigns (drafts
 * and not-yet-sent rows have nothing meaningful to show).
 */

const STATUS_CHIP_STYLES = {
    [CAMPAIGN_STATUS.DRAFT]: { backgroundColor: '#E8E8E8', color: '#555' },
    [CAMPAIGN_STATUS.SCHEDULED]: { backgroundColor: '#E3F2FD', color: '#1565C0' },
    [CAMPAIGN_STATUS.ONGOING]: { backgroundColor: '#FFF4E5', color: '#B26A00' },
    [CAMPAIGN_STATUS.SENT]: { backgroundColor: '#D4EDDA', color: '#155724' },
    [CAMPAIGN_STATUS.FAILED]: { backgroundColor: '#FDECEA', color: '#B71C1C' },
};

const STATUS_LABEL = {
    [CAMPAIGN_STATUS.DRAFT]: 'Draft',
    [CAMPAIGN_STATUS.SCHEDULED]: 'Scheduled',
    [CAMPAIGN_STATUS.ONGOING]: 'Ongoing',
    [CAMPAIGN_STATUS.SENT]: 'Sent',
    [CAMPAIGN_STATUS.FAILED]: 'Failed',
};

const statusChip = (status) => {
    const label = STATUS_LABEL[status] || 'Draft';
    const style = STATUS_CHIP_STYLES[status] || STATUS_CHIP_STYLES[CAMPAIGN_STATUS.DRAFT];
    return <Chip size="small" label={label} sx={{ ...style, fontWeight: 500 }} />;
};

// Resolves what to show in the "Sent" column based on the campaign's
// lifecycle stage. Drafts and Failed rows have no meaningful delivery
// timestamp — they show "—". Scheduled rows show the planned fire time
// (italic, since it hasn't happened yet), Ongoing shows a "Sending…"
// indicator, and Sent shows the actual send timestamp.
const deliveryByContent = (row) => {
    if (!row) return { label: '—', muted: true };
    if (row.status === CAMPAIGN_STATUS.SENT && row.sent_at) {
        return { label: moment(row.sent_at).format('MMM D, YYYY, h:mm A'), muted: false };
    }
    if (row.status === CAMPAIGN_STATUS.ONGOING) {
        return { label: 'Sending…', muted: false };
    }
    if (row.status === CAMPAIGN_STATUS.SCHEDULED && row.scheduled_at) {
        return {
            label: `Scheduled for ${moment(row.scheduled_at).format('MMM D, YYYY, h:mm A')} ${operatorLocalTimeLabel()}`,
            muted: true,
        };
    }
    return { label: '—', muted: true };
};

// Lifetime sends across every channel — mirrors ``totalSent`` in
// CampaignReport.js so the list and the detail Results tab agree.
const totalSentFor = (row) =>
    (Number(row?.billing_chat_count) || 0) +
    (Number(row?.billing_sms_count) || 0) +
    (Number(row?.billing_whatsapp_count) || 0) +
    (Number(row?.billing_email_count) || 0) +
    (Number(row?.billing_airbnb_count) || 0) +
    (Number(row?.billing_push_count) || 0);

const formatPct = (value) => `${Math.round(Number(value) || 0)}%`;

// Delivered share of the sends that have a result (delivered + failed), kept
// by the backend's metrics rollup; sends still awaiting confirmation don't
// count yet ("—" while nothing has settled). Null for a campaign whose
// delivery isn't shown (operator not switched on, nothing trackable, or an
// older campaign not estimated) and missing on an older backend: the old
// 100% stand-in, as in CampaignReport.js.
const deliveryPctLabel = (row) => {
    const trackable = Number(row.delivery_trackable_count) || 0;
    if (trackable <= 0) return formatPct(100);
    const delivered = Number(row.delivered_count) || 0;
    const settled = delivered + (Number(row.delivery_failed_count) || 0);
    if (settled <= 0) return '—';
    return formatPct((delivered / settled) * 100);
};

// Delivery / Open / Reply triple for the list. Only meaningful once a
// campaign has actually sent, so anything else shows a single muted dash.
export const engagementContent = (row) => {
    if (!row || row.status !== CAMPAIGN_STATUS.SENT) return { label: '—', muted: true };
    const totalSent = totalSentFor(row);
    if (totalSent <= 0) return { label: '—', muted: true };
    const openedPct = Number(row.opened_pct) || 0;
    const mauReply =
        (Number(row.mau_participant_added_reply_count) || 0) +
        (Number(row.mau_user_created_reply_count) || 0);
    const repliedPct = (mauReply / totalSent) * 100;
    return {
        label: `${deliveryPctLabel(row)} / ${formatPct(openedPct)} / ${formatPct(repliedPct)}`,
        muted: false,
    };
};

const audienceText = (row) => {
    if (!row) return '—';
    if (row.last_audience_count != null) return Number(row.last_audience_count).toLocaleString();
    // Use whichever filter list applies to the campaign's chosen send_as
    // mode: announcement campaigns store their filters under
    // ``audience_channel_filters``, everything else under ``audience_filters``.
    const isAnnouncement = row.send_as === 'announcement';
    const list = isAnnouncement ? row.audience_channel_filters : row.audience_filters;
    const filters = Array.isArray(list) ? list.length : 0;
    if (filters === 0) return isAnnouncement ? 'All channels' : 'All guests';
    return `${filters} filter${filters === 1 ? '' : 's'}`;
};

// Column widths are user-adjustable (CP-8144) and survive reloads. Stored as
// { [field]: number } — plain px values, same shape TripUserManager uses.
const COLUMN_WIDTHS_KEY = 'CampaignList.columnWidths.v1';

const readSavedWidths = () => {
    try {
        const raw = JSON.parse(localStorage.getItem(COLUMN_WIDTHS_KEY) || '{}');
        const clean = {};
        for (const [field, width] of Object.entries(raw)) {
            if (typeof width === 'number' && Number.isFinite(width) && width > 0) clean[field] = width;
        }
        return clean;
    } catch {
        return {};
    }
};

const CampaignListGrid = ({ rows, classes, campaignQuery, handlers }) => {
    const [columnWidths, setColumnWidths] = React.useState(readSavedWidths);

    const onColumnWidthChange = React.useCallback((params) => {
        const width = params?.width;
        if (!Number.isFinite(width)) return;
        setColumnWidths((prev) => {
            const next = { ...prev, [params.colDef.field]: width };
            try {
                localStorage.setItem(COLUMN_WIDTHS_KEY, JSON.stringify(next));
            } catch {
                // Ignore localStorage errors (private mode, quota) — the resize
                // still applies for this session.
            }
            return next;
        });
    }, []);

    const filtered = React.useMemo(() => {
        const q = (campaignQuery || '').trim().toLowerCase();
        if (!q) return rows;
        return rows.filter(
            (r) =>
                String(r.name || '').toLowerCase().includes(q) ||
                String(r.message_body || '').toLowerCase().includes(q),
        );
    }, [rows, campaignQuery]);

    const columns = React.useMemo(
        () => [
            {
                field: 'name',
                headerName: 'Name',
                flex: 1,
                minWidth: 220,
                sortable: true,
                renderCell: (p) =>
                    p.row ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                            <CampaignOutlinedIcon fontSize="small" color="action" />
                            <Typography noWrap fontWeight={500}>
                                {p.row.name}
                            </Typography>
                        </div>
                    ) : null,
            },
            {
                field: 'audience',
                headerName: 'Audience',
                width: 130,
                sortable: false,
                renderCell: (p) => <Typography>{audienceText(p.row)}</Typography>,
            },
            {
                field: 'delivery_by',
                headerName: 'Sent',
                flex: 0.7,
                minWidth: 170,
                sortable: false,
                renderCell: (p) => {
                    const { label, muted } = deliveryByContent(p.row);
                    return <Typography color={muted ? 'text.secondary' : 'inherit'}>{label}</Typography>;
                },
            },
            {
                field: 'status',
                headerName: 'Status',
                width: 120,
                sortable: true,
                renderCell: (p) => (p.row ? statusChip(p.row.status) : null),
            },
            {
                field: 'engagement',
                headerName: 'Delivery / Open / Reply',
                width: 190,
                sortable: false,
                renderCell: (p) => {
                    const { label, muted } = engagementContent(p.row);
                    return <Typography color={muted ? 'text.secondary' : 'inherit'}>{label}</Typography>;
                },
            },
            {
                field: 'created_ts',
                headerName: 'Created',
                width: 160,
                sortable: true,
                renderCell: (p) => (
                    <Typography>{p.row?.created_ts ? moment(p.row.created_ts).format('MMM D, YYYY') : '—'}</Typography>
                ),
            },
            {
                field: 'buttons',
                headerName: 'Action',
                sortable: false,
                width: 175,
                align: 'right',
                headerAlign: 'right',
                renderCell: (p) => (p.row ? CampaignActionsColumn(p, handlers) : null),
            },
        ],
        [handlers],
    );

    // Bake saved widths into the definitions so a resized column keeps its width
    // across re-renders and reloads. ``flex`` has to go with it — a flex column
    // ignores ``width`` and would snap back to its share of the grid.
    const sizedColumns = React.useMemo(
        () =>
            columns.map((col) => {
                const width = columnWidths[col.field];
                if (!width) return col;
                const { flex: _flex, ...rest } = col;
                return { ...rest, width };
            }),
        [columns, columnWidths],
    );

    return (
        <DataGridPro
            className={classes?.recordList}
            rows={filtered}
            columns={sizedColumns}
            getRowId={(r) => r.id}
            disableRowSelectionOnClick
            disableColumnMenu
            // Pro enables column reordering by default; we only want resizing
            // here (CP-8144) — a reorder that resets on reload just confuses.
            disableColumnReorder
            pagination
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            pageSizeOptions={[10, 25, 50]}
            onColumnWidthChange={onColumnWidthChange}
            sx={{
                border: 'none',
                '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
            }}
        />
    );
};

export default CampaignListGrid;
